import React, { useEffect, useState } from 'react'
import classNames from 'classnames'

import Checkbox from '_components/checkbox'
import { AC_FILTERS } from './constants'

// Map category name -> top level (computed once)
const CATEGORY_TO_TOP_LEVEL = Object.fromEntries(
  AC_FILTERS.map(({ category, topLevel }) => [category.name, topLevel])
)

const FiltersControl = ({
  data,
  openTarget,
  topLevel,
  setTopLevel,
  selectedCategory,
  setSelectedCategory,
  handleCheckboxChange,
  handlePercentileChange,
}) => {
  const handleSelectCategory = (name) => setSelectedCategory(name)

  const getItemTopLevel = (item) =>
    item?.topLevel || CATEGORY_TO_TOP_LEVEL[item?.category?.name]

  // Determine which top-levels are actually present in the provided data
  const presentTopLevels = Array.from(
    new Set(
      data.map((item) => getItemTopLevel(item)).filter(Boolean)
    )
  )

  const hasMultipleTopLevels = presentTopLevels.length > 1
  const showTopLevelPane = openTarget === 'initial' && hasMultipleTopLevels

  // If we need a top-level selection but it's not set or invalid, default it
  useEffect(() => {
    if (!showTopLevelPane) return
    if (!topLevel || !presentTopLevels.includes(topLevel)) {
      setTopLevel?.(presentTopLevels[0])
    }
  }, [showTopLevelPane, topLevel, presentTopLevels, setTopLevel])

  // Narrow categories by selected top-level when relevant
  const filteredCategories = showTopLevelPane
    ? data.filter((item) => getItemTopLevel(item) === topLevel)
    : data

  // Show categories pane only if needed:
  // - If showing top-levels: only show categories when that top-level has more than one category
  // - If not showing top-levels: keep showing categories as before
  const showCategoriesPane = openTarget === 'initial' && (!showTopLevelPane || filteredCategories.length > 1)

  // Helpers to compute selected counts
  const getCategorySelectedCount = (categoryItem) =>
    categoryItem?.values?.reduce((acc, value) => acc + (value.check ? 1 : 0), 0) ?? 0

  const topLevelSelectedCountMap = (() => {
    const counts = Object.fromEntries(presentTopLevels.map((label) => [label, 0]))
    data.forEach((item) => {
      const tl = getItemTopLevel(item)
      if (!tl) return
      counts[tl] += getCategorySelectedCount(item)
    })
    return counts
  })()

  // Ensure selectedCategory belongs to the currently visible (filtered) categories
  useEffect(() => {
    if (openTarget !== 'initial') return
    const existsInFiltered = filteredCategories.some(
      ({ category }) => category.name === selectedCategory
    )
    if (!existsInFiltered && filteredCategories.length) {
      setSelectedCategory(filteredCategories[0].category.name)
    }
  }, [openTarget, filteredCategories, selectedCategory, setSelectedCategory])

  const currentCategory =
    filteredCategories.find(({ category }) => category.name === selectedCategory) ||
    filteredCategories[0] ||
    data[0]

  const [query, setQuery] = useState('')

  const handleSearch = (event) => {
    setQuery(event.target.value)
  }

  return (
    <div
      className={classNames('filters-control', {
        'filters-control--initial': openTarget === 'initial',
        'filters-control--with-top-level': showTopLevelPane,
        'filters-control--with-categories': showCategoriesPane,
      })}
    >
      <If condition={openTarget === 'initial'}>
        <If condition={showTopLevelPane}>
          <div className="filters-control__top-levels mac-scrollbar">
            {presentTopLevels.map((label) => (
              <button
                key={label}
                onClick={() => setTopLevel?.(label)}
                className={classNames('filters-control__category', {
                  'filters-control__category--active': topLevel === label,
                })}
              >
                {label}
                {topLevelSelectedCountMap[label] > 0 && (
                  <span>{topLevelSelectedCountMap[label]}</span>
                )}
              </button>
            ))}
          </div>
        </If>
        <If condition={showCategoriesPane}>
          <div className="filters-control__categories mac-scrollbar">
            {filteredCategories.map((item) => (
              <button
                key={item.category.name}
                onClick={() => handleSelectCategory(item.category.name)}
                className={classNames('filters-control__category', {
                  'filters-control__category--active':
                    currentCategory?.category?.name === item.category.name,
                })}
              >
                {item.category.label}
                {getCategorySelectedCount(item) > 0 && (
                  <span>{getCategorySelectedCount(item)}</span>
                )}
              </button>
            ))}
          </div>
        </If>
      </If>
      <div className="filters-control__values mac-scrollbar">
        <input
          type="search"
          name=""
          id=""
          placeholder="Search"
          value={query}
          onChange={handleSearch}
        />
        {currentCategory?.values
          .filter((value) => !query || value.label.toLowerCase().includes(query.toLowerCase()))
          .map((value) => (
            <div key={value.name + value.label}>
              <Checkbox
                name={value.name}
                label={value.label}
                checked={value.check}
                onChange={handleCheckboxChange(currentCategory, value)}
              />
              <If condition={currentCategory?.category?.name?.startsWith?.('predictor_') && value.check}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 28, color: '#3d3d3d' }}>
                  <span>Top</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={Math.max(0, Math.min(100, 100 - (Number(value?.percentile ?? 0))))}
                    onChange={handlePercentileChange(currentCategory, value)}
                    style={{ width: 64 }}
                  />
                  <span>%</span>
                </div>
              </If>
            </div>
          ))}
      </div>
    </div>
  )
}

export default FiltersControl
